import { useApiMutation, useInvalidateQueries } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import {
  UpdateContactMemoRequest,
  UpdateContactStatusRequest,
} from '@/Users/types/userTypes';

export const useContactMutations = (contactId: number, userUuid?: string) => {
  const { invalidateQueries } = useInvalidateQueries();

  const updateMemoMutation = useApiMutation<
    null,
    Error,
    UpdateContactMemoRequest
  >(
    (memoData) =>
      api.patch<null>(`/api/v1/contact/memo/${contactId}`, memoData),
    {
      onSuccess: () => {
        invalidateQueries(['contact', 'detail', contactId]);
        if (userUuid) {
          invalidateQueries(['user', 'detail', userUuid]);
        }
      },
      onError: (error: any) => {
        alert(`Failed to update memo: ${error?.message || 'Unknown error'}`);
      },
    },
  );

  const updateStatusMutation = useApiMutation<
    null,
    Error,
    UpdateContactStatusRequest
  >(
    (statusData) =>
      api.patch<null>(`/api/v1/contact/status/${contactId}`, statusData),
    {
      onSuccess: () => {
        invalidateQueries(['contact', 'detail', contactId]);
        if (userUuid) {
          invalidateQueries(['user', 'detail', userUuid]);
        }
      },
      onError: (error: any) => {
        alert(`Failed to update status: ${error?.message || 'Unknown error'}`);
      },
    },
  );

  const updateContactMutation = useApiMutation<
    null,
    Error,
    { memo?: string; status?: 'READ' | 'UNREAD' }
  >(
    async (data) => {
      const promises = [];

      if (data.memo !== undefined) {
        promises.push(
          api.patch<null>(`/api/v1/contact/memo/${contactId}`, {
            memo: data.memo,
          }),
        );
      }

      if (data.status !== undefined) {
        promises.push(
          api.patch<null>(`/api/v1/contact/status/${contactId}`, {
            status: data.status,
          }),
        );
      }

      await Promise.all(promises);
      return {
        success: true,
        message: 'Contact updated successfully',
        statusCode: 200,
        data: null,
      };
    },
    {
      onSuccess: () => {
        invalidateQueries(['contact', 'detail', contactId]);
        if (userUuid) {
          invalidateQueries(['user', 'detail', userUuid]);
        }
      },
      onError: (error: any) => {
        alert(`Failed to update contact: ${error?.message || 'Unknown error'}`);
      },
    },
  );

  return {
    updateMemo: (memo: string) => {
      updateMemoMutation.mutate({ memo });
    },
    updateStatus: (status: 'READ' | 'UNREAD') => {
      updateStatusMutation.mutate({ status });
    },
    updateContact: (data: { memo?: string; status?: 'READ' | 'UNREAD' }) => {
      updateContactMutation.mutate(data);
    },
    isUpdatingMemo: updateMemoMutation.isPending,
    isUpdatingStatus: updateStatusMutation.isPending,
    isUpdatingContact: updateContactMutation.isPending,
    isLoading:
      updateMemoMutation.isPending ||
      updateStatusMutation.isPending ||
      updateContactMutation.isPending,
  };
};
